import { useEffect } from 'react'

import analyticsService from '../services/analyticsService'
import { isPrivacyCategoryAllowed } from '@/privacy/privacyPreferences'
import logger from '@utils/logger'

const THRESHOLDS = {
  CLS: 0.1,
  FID: 100,
  INP: 200,
  FCP: 1800,
  LCP: 2500,
  TTFB: 600
}

let reportingEnabled = false
let initializationPromise = null

function isAuthenticated() {
  try {
    const cachedUser = localStorage.getItem('user')
    if (!cachedUser) return false

    const user = JSON.parse(cachedUser)
    return Boolean(user?.id)
  } catch (_error) {
    return false
  }
}

function reportWebVital(metric) {
  if (
    !reportingEnabled ||
    !isPrivacyCategoryAllowed('analytics') ||
    !isAuthenticated()
  ) {
    return
  }

  void analyticsService.trackWebVital(metric)

  if (metric.value > THRESHOLDS[metric.name]) {
    logger.warn(`Poor ${metric.name} performance:`, {
      value: Math.round(metric.value),
      threshold: THRESHOLDS[metric.name],
      rating: metric.rating
    })
  }
}

function initializeWebVitalsReporting() {
  if (initializationPromise) return initializationPromise

  initializationPromise = import('web-vitals')
    .then(({ onCLS, onFID, onFCP, onLCP, onTTFB, onINP }) => {
      if (onCLS) onCLS(reportWebVital)
      if (onFID) onFID(reportWebVital)
      if (onINP) onINP(reportWebVital)
      if (onFCP) onFCP(reportWebVital)
      if (onLCP) onLCP(reportWebVital)
      if (onTTFB) onTTFB(reportWebVital)
    })
    .catch(error => {
      initializationPromise = null
      logger.warn('Web Vitals not available:', error)
    })

  return initializationPromise
}

export const useWebVitals = (enabled = false) => {
  useEffect(() => {
    reportingEnabled = Boolean(enabled && isAuthenticated())

    if (reportingEnabled) void initializeWebVitalsReporting()

    return () => {
      reportingEnabled = false
    }
  }, [enabled])
}
